import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { Subscription } from 'rxjs';
import { Company } from '../../company/company.model';
import { TenantScope } from '../../company/tenant-access.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric, PageMetricTone } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmQuotationJobType, CrmQuotationStatus, CrmQuotationType } from '../crm.model';
import {
  CrmDashboardActivityRow,
  CrmDashboardChartPoint,
  CrmDashboardFunnelStage,
  CrmDashboardOverview,
  CrmDashboardQuotationRow,
  CrmDashboardRiskRow,
  CrmDashboardScorecard
} from './crm-dashboard.model';
import { CrmDashboardService } from './crm-dashboard.service';

interface CrmDashboardFilters {
  fromDate: Date | null;
  toDate: Date | null;
  customer: string;
  source: string;
  ownerId: number | null;
  leadStatus: string | null;
  prospectStatus: string | null;
  quotationStatus: string | null;
  quotationType: string | null;
  jobType: string | null;
  businessType: string | null;
  quality: string | null;
  currencyCode: string | null;
}

@Component({
  selector: 'app-crm-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    ChartModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    DashboardCompanyScopeSelectorComponent,
    PageDataPanelComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageShellComponent
  ],
  templateUrl: './crm-dashboard.component.html',
  styleUrls: ['./crm-dashboard.component.css']
})
export class CrmDashboardComponent implements OnInit, OnDestroy {
  isLoading = false;
  companyId: number | null = null;
  companyGroupId: number | null = null;
  activeGroupName = '';
  groupCompanies: Company[] = [];
  selectedGroupCompanyId: number | null = null;
  private readonly subscriptions = new Subscription();
  filters: CrmDashboardFilters = this.defaultFilters();
  overview: CrmDashboardOverview = {};

  breadcrumbItems: MenuItem[] = [
    { label: 'CRM' },
    { label: 'Dashboard' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  leadStatusOptions = [
    { label: 'New', value: 'NEW' },
    { label: 'Qualified', value: 'QUALIFIED' },
    { label: 'Negotiation', value: 'NEGOTIATION' },
    { label: 'Lost', value: 'LOST' }
  ];
  prospectStatusOptions = [
    { label: 'New', value: 'NEW' },
    { label: 'Qualified', value: 'QUALIFIED' },
    { label: 'Proposal', value: 'PROPOSAL' },
    { label: 'Lost', value: 'LOST' }
  ];
  quotationStatusOptions = Object.values(CrmQuotationStatus).map(value => ({ label: this.label(value), value }));
  quotationTypeOptions = Object.values(CrmQuotationType).map(value => ({ label: this.label(value), value }));
  jobTypeOptions = Object.values(CrmQuotationJobType).map(value => ({ label: this.label(value), value }));
  businessTypeOptions = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];
  qualityOptions = [
    { label: 'High', value: 'HIGH' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Low', value: 'LOW' }
  ];
  currencyOptions = ['SAR', 'USD', 'EUR', 'AED'].map(value => ({ label: value, value }));

  leadSourceChartData: any = this.emptyChartData();
  leadQualityChartData: any = this.emptyChartData();
  quotationStatusChartData: any = this.emptyChartData();
  quotationTypeChartData: any = this.emptyChartData();
  jobTypeChartData: any = this.emptyChartData();
  pipelineTrendChartData: any = this.emptyTrendChartData();
  acquisitionTrendChartData: any = this.emptyTrendChartData();
  ownerRadarChartData: any = this.emptyChartData();
  quotationFunnelChartData: any = this.emptyChartData();

  heatmapDays: string[] = [];
  heatmapTypes: string[] = [];
  heatmapMax = 0;

  private readonly palette = ['#0f766e', '#2563eb', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#16a34a', '#64748b', '#ea580c'];

  chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, usePointStyle: true } }
    },
    scales: {
      x: { grid: { color: '#edf2f7' } },
      y: { beginAtZero: true, grid: { color: '#edf2f7' } }
    }
  };

  lineChartOptions = {
    maintainAspectRatio: false,
    tension: 0.35,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, usePointStyle: true } }
    },
    scales: {
      x: { grid: { color: '#edf2f7' } },
      y: {
        beginAtZero: true,
        grid: { color: '#edf2f7' },
        ticks: { callback: (value: string | number) => this.compactNumber(value) }
      }
    }
  };

  funnelChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: { beginAtZero: true, grid: { color: '#edf2f7' } },
      y: { grid: { display: false } }
    }
  };

  constructor(
    private dashboardService: CrmDashboardService,
    private tenantScopeService: TenantScopeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => this.handleScopeChange(scope)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get metrics(): PageMetric[] {
    const cards = this.overview.scorecards || [];
    if (cards.length) {
      return cards.map(card => ({
        label: card.label || this.label(card.key),
        value: this.scorecardValue(card),
        hint: this.scorecardHint(card),
        tone: this.metricTone(card.tone)
      }));
    }

    const summary = this.overview.summary || {};
    return [
      { label: 'Lead Intake', value: this.formatNumber(summary.totalLeads), hint: `${this.formatNumber(summary.openLeads)} open`, tone: 'info' },
      { label: 'Open Pipeline', value: this.money(summary.pipelineValue), hint: `${this.money(summary.weightedPipelineValue)} weighted`, tone: 'info' },
      { label: 'Quote Win Rate', value: this.percent(summary.quoteWinRate), hint: `${this.formatNumber(summary.wonQuotations)} won`, tone: 'success' },
      { label: 'Overdue Follow-ups', value: this.formatNumber(summary.overdueReminders), hint: `${this.formatNumber(summary.openReminders)} open tasks`, tone: (summary.overdueReminders || 0) > 0 ? 'danger' : 'success' },
      { label: 'At-risk Pipeline', value: this.money(summary.atRiskPipelineValue), hint: `${this.formatNumber(summary.atRiskItems)} risk items`, tone: (summary.atRiskItems || 0) > 0 ? 'warn' : 'success' },
      { label: 'Avg Sales Cycle', value: this.days(summary.averageSalesCycleDays), hint: `${this.days(summary.averageOpenQuoteAgeDays)} open quote age`, tone: 'default' }
    ];
  }

  get activeFilterCount(): number {
    return Object.entries(this.filters).filter(([_, value]) => value !== null && value !== '').length;
  }

  get filterResultText(): string {
    const summary = this.overview.summary || {};
    return `${this.formatNumber(summary.openQuotations)} open quotations | ${this.formatNumber(summary.atRiskItems)} risk items`;
  }

  get showGroupCompanySelector(): boolean {
    return !!this.companyGroupId && this.groupCompanies.length > 0;
  }

  loadDashboard(): void {
    if (!this.companyId && !this.companyGroupId) return;
    this.isLoading = true;
    this.dashboardService.getOverview(this.buildParams())
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: overview => {
          this.overview = overview || {};
          this.buildDerivedViews();
        },
        error: () => {
          this.overview = {};
          this.buildDerivedViews();
        }
      });
  }

  clearFilters(): void {
    this.filters = this.defaultFilters();
    this.loadDashboard();
  }

  openLead(id?: number): void {
    if (id) this.router.navigate(['/crm/leads', id]);
  }

  openProspect(id?: number): void {
    if (id) this.router.navigate(['/crm/prospects', id]);
  }

  openClient(id?: number): void {
    if (id) this.router.navigate(['/crm/clients', id]);
  }

  onDashboardCompanyScopeChange(companyId: number | null): void {
    this.selectedGroupCompanyId = companyId;
    this.loadDashboard();
  }

  openQuotation(id?: number): void {
    if (id) this.router.navigate(['/crm/quotations', id]);
  }

  openAccount(row?: { clientId?: number; customerId?: number }): void {
    if (row?.clientId) {
      this.openClient(row.clientId);
    }
  }

  openRisk(row: CrmDashboardRiskRow): void {
    this.openEntity(row.entityType, row.entityId);
  }

  openActivity(row: CrmDashboardActivityRow): void {
    this.openEntity(row.relationType, row.relationId);
  }

  openEntity(entityType?: string, entityId?: number): void {
    const type = (entityType || '').toLowerCase();
    if (!entityId) return;
    if (type.includes('quotation')) this.openQuotation(entityId);
    else if (type.includes('lead')) this.openLead(entityId);
    else if (type.includes('prospect')) this.openProspect(entityId);
    else if (type.includes('client') || type.includes('account')) this.openClient(entityId);
  }

  tagSeverity(value?: string): string {
    switch ((value || '').toLowerCase()) {
      case 'good':
      case 'success':
      case 'approved':
      case 'completed':
      case 'job':
      case 'qualified':
      case 'high':
        return 'success';
      case 'warn':
      case 'warning':
      case 'medium':
      case 'proposal':
      case 'negotiation':
      case 'submitted':
      case 'client_review':
      case 'revision_requested':
      case 'ready_to_submit':
      case 'preparing':
        return 'warn';
      case 'danger':
      case 'critical':
      case 'low':
      case 'lost':
      case 'rejected':
      case 'expired':
      case 'overdue':
        return 'danger';
      case 'info':
      case 'new':
      case 'internal_review':
        return 'info';
      default:
        return 'secondary';
    }
  }

  metricTone(value?: string): PageMetricTone {
    switch ((value || '').toLowerCase()) {
      case 'good':
      case 'success':
        return 'success';
      case 'warn':
      case 'warning':
        return 'warn';
      case 'danger':
      case 'critical':
        return 'danger';
      case 'neutral':
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  }

  money(value?: number, currency = this.displayCurrency()): string {
    return `${this.formatNumber(value, 2)} ${currency}`;
  }

  percent(value?: number): string {
    return `${this.formatNumber(value, 1)}%`;
  }

  days(value?: number): string {
    return `${this.formatNumber(value, 1)}d`;
  }

  formatNumber(value?: number, maximumFractionDigits = 0): string {
    return (value || 0).toLocaleString(undefined, { maximumFractionDigits });
  }

  compactNumber(value: string | number): string {
    const amount = Number(value) || 0;
    const absolute = Math.abs(amount);
    if (absolute >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
    if (absolute >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
    return `${amount}`;
  }

  label(value?: string): string {
    if (!value) return '-';
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, match => match.toUpperCase());
  }

  scorecardValue(card: CrmDashboardScorecard): string {
    const value = card.value || 0;
    switch ((card.unit || '').toLowerCase()) {
      case 'amount':
        return this.money(value);
      case 'percent':
        return this.percent(value);
      case 'days':
        return this.days(value);
      default:
        return this.formatNumber(value, 1);
    }
  }

  scorecardHint(card: CrmDashboardScorecard): string {
    if (!card.secondaryLabel) return card.description || '';
    const secondary = card.secondaryValue || 0;
    const key = `${card.key || ''} ${card.secondaryLabel}`.toLowerCase();
    const formatted = key.includes('quote') || key.includes('pipeline')
      ? this.money(secondary)
      : key.includes('age') || key.includes('cycle')
        ? this.days(secondary)
        : this.formatNumber(secondary, 1);
    return `${card.secondaryLabel}: ${formatted}`;
  }

  funnelWidth(stage: CrmDashboardFunnelStage): number {
    return Math.max(3, Math.min(100, stage.conversionPercent || 0));
  }

  funnelAmount(stage: CrmDashboardFunnelStage): string {
    return stage.amount ? this.money(stage.amount) : this.formatNumber(stage.count);
  }

  heatmapCount(activityType: string, day: string): number {
    return this.overview.activityHeatmap?.find(cell => cell.activityType === activityType && cell.day === day)?.count || 0;
  }

  heatmapColor(count: number): string {
    if (!count || !this.heatmapMax) return '#f8fafc';
    const opacity = Math.min(0.9, 0.16 + (count / this.heatmapMax) * 0.72);
    return `rgba(15, 118, 110, ${opacity})`;
  }

  heatmapLabel(value?: string): string {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  quoteRiskLabel(row: CrmDashboardQuotationRow): string {
    if (row.riskReason) return row.riskReason;
    if ((row.daysToExpiry || 0) < 0) return 'Expired';
    if ((row.daysToExpiry || 0) <= 7) return 'Expiring';
    return row.riskLevel || 'Normal';
  }

  private buildParams(): HttpParams {
    let params = new HttpParams();
    if (this.companyGroupId) {
      params = params.set('companyGroupId', String(this.companyGroupId));
      if (this.selectedGroupCompanyId) {
        params = params.set('memberCompanyId', String(this.selectedGroupCompanyId));
      }
    } else if (this.companyId) {
      params = params.set('companyId', String(this.companyId));
    }
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value === null || value === '') return;
      params = params.set(key, value instanceof Date ? this.formatDate(value) : String(value));
    });
    return params;
  }

  private buildDerivedViews(): void {
    this.leadSourceChartData = this.toPointChartData(this.overview.leadsBySource || [], 'count');
    this.leadQualityChartData = this.toPointChartData(this.overview.leadsByQuality || [], 'count');
    this.quotationStatusChartData = this.toPointChartData(this.overview.quotationStatusBreakdown || [], 'amount');
    this.quotationTypeChartData = this.toPointBarData(this.overview.quotationTypeBreakdown || [], 'amount', 'Quoted Value');
    this.jobTypeChartData = this.toPointBarData(this.overview.quotationJobTypeBreakdown || [], 'count', 'Quotations');
    this.pipelineTrendChartData = this.toPipelineTrendData();
    this.acquisitionTrendChartData = this.toAcquisitionTrendData();
    this.ownerRadarChartData = this.toOwnerRadarData();
    this.quotationFunnelChartData = this.toFunnelChartData();
    this.buildHeatmap();
  }

  private toPointChartData(items: CrmDashboardChartPoint[], field: 'count' | 'amount' | 'weightedAmount') {
    const rows = items.filter(item => (item[field] || 0) > 0);
    if (!rows.length) return this.emptyChartData();
    return {
      labels: rows.map(item => item.label || this.label(item.key)),
      datasets: [{
        data: rows.map(item => item[field] || 0),
        backgroundColor: this.palette,
        borderColor: '#ffffff',
        borderWidth: 2
      }]
    };
  }

  private toPointBarData(items: CrmDashboardChartPoint[], field: 'count' | 'amount' | 'weightedAmount', label: string) {
    const rows = items.filter(item => (item[field] || 0) > 0);
    if (!rows.length) return this.emptyChartData();
    return {
      labels: rows.map(item => item.label || this.label(item.key)),
      datasets: [{
        label,
        data: rows.map(item => item[field] || 0),
        backgroundColor: rows.map((_, index) => this.palette[index % this.palette.length]),
        borderRadius: 6
      }]
    };
  }

  private toPipelineTrendData() {
    const rows = this.overview.pipelineTrend || [];
    if (!rows.length) return this.emptyTrendChartData();
    return {
      labels: rows.map(row => row.period || '-'),
      datasets: [
        { label: 'Pipeline', data: rows.map(row => row.pipelineValue || 0), borderColor: '#0f766e', backgroundColor: 'rgba(15, 118, 110, 0.12)', fill: true, tension: 0.35 },
        { label: 'Weighted', data: rows.map(row => row.weightedPipelineValue || 0), borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.12)', fill: true, tension: 0.35 },
        { label: 'Won', data: rows.map(row => row.wonValue || 0), borderColor: '#2563eb', backgroundColor: 'rgba(37, 99, 235, 0.12)', fill: false, tension: 0.35 }
      ]
    };
  }

  private toAcquisitionTrendData() {
    const rows = this.overview.acquisitionTrend || [];
    if (!rows.length) return this.emptyTrendChartData();
    return {
      labels: rows.map(row => row.period || '-'),
      datasets: [
        { label: 'Leads', data: rows.map(row => row.leads || 0), backgroundColor: '#0f766e', borderRadius: 6 },
        { label: 'Prospects', data: rows.map(row => row.prospects || 0), backgroundColor: '#2563eb', borderRadius: 6 },
        { label: 'Clients', data: rows.map(row => row.clients || 0), backgroundColor: '#16a34a', borderRadius: 6 },
        { label: 'Quotations', data: rows.map(row => row.quotations || 0), backgroundColor: '#f59e0b', borderRadius: 6 }
      ]
    };
  }

  private toOwnerRadarData() {
    const rows = (this.overview.ownerPerformance || []).slice(0, 6);
    if (!rows.length) return this.emptyChartData();
    return {
      labels: rows.map(row => row.ownerLabel || `Owner ${row.ownerId || '-'}`),
      datasets: [
        { label: 'Pipeline', data: rows.map(row => row.pipelineValue || 0), borderColor: '#0f766e', backgroundColor: 'rgba(15, 118, 110, 0.2)' },
        { label: 'Won', data: rows.map(row => row.wonValue || 0), borderColor: '#2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)' },
        { label: 'Overdue Tasks', data: rows.map(row => row.overdueReminders || 0), borderColor: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.14)' }
      ]
    };
  }

  private toFunnelChartData() {
    const rows = this.overview.quotationFunnel || [];
    if (!rows.length) return this.emptyChartData();
    return {
      labels: rows.map(row => row.label || this.label(row.key)),
      datasets: [{
        data: rows.map(row => row.count || 0),
        backgroundColor: rows.map((_, index) => this.palette[index % this.palette.length]),
        borderRadius: 6
      }]
    };
  }

  private buildHeatmap(): void {
    const cells = this.overview.activityHeatmap || [];
    this.heatmapDays = [...new Set(cells.map(cell => cell.day).filter(Boolean) as string[])].sort();
    this.heatmapTypes = [...new Set(cells.map(cell => cell.activityType).filter(Boolean) as string[])].sort();
    this.heatmapMax = Math.max(0, ...cells.map(cell => cell.count || 0));
  }

  private emptyChartData() {
    return {
      labels: ['No data'],
      datasets: [{ data: [1], backgroundColor: ['#e2e8f0'], borderColor: '#ffffff', borderWidth: 2 }]
    };
  }

  private emptyTrendChartData() {
    return { labels: [], datasets: [] };
  }

  private defaultFilters(): CrmDashboardFilters {
    return {
      fromDate: null,
      toDate: null,
      customer: '',
      source: '',
      ownerId: null,
      leadStatus: null,
      prospectStatus: null,
      quotationStatus: null,
      quotationType: null,
      jobType: null,
      businessType: null,
      quality: null,
      currencyCode: null
    };
  }

  private displayCurrency(): string {
    return this.filters.currencyCode || 'SAR';
  }

  private formatDate(value: Date): string {
    return value.toISOString().slice(0, 10);
  }

  private handleScopeChange(scope: TenantScope | null): void {
    const nextCompanyId = scope?.type === 'COMPANY' ? scope.companyId : null;
    const nextCompanyGroupId = scope?.type === 'GROUP' ? scope.companyGroupId : null;
    this.activeGroupName = scope?.type === 'GROUP' ? scope.companyGroupName : '';
    this.groupCompanies = scope?.type === 'GROUP' ? (scope.memberCompanies ?? []) : [];
    if (!this.groupCompanies.some(company => company.id === this.selectedGroupCompanyId)) {
      this.selectedGroupCompanyId = null;
    }
    if (this.companyId === nextCompanyId && this.companyGroupId === nextCompanyGroupId) {
      return;
    }

    this.companyId = nextCompanyId;
    this.companyGroupId = nextCompanyGroupId;
    this.selectedGroupCompanyId = null;
    if (!this.companyId && !this.companyGroupId) {
      this.overview = {};
      this.buildDerivedViews();
      return;
    }
    this.loadDashboard();
  }
}
